import { supabase } from '../lib/supabase'
import type { TeamMember } from '../types'

function rowToMember(row: Record<string, unknown>): TeamMember {
  return {
    id:              row.id as string,
    name:            (row.full_name as string) || 'Unknown',
    role:            (row.job_title as string) || (row.role as string) || 'Team Member',
    email:           (row.email as string) || '',
    skills:          (row.skills as string[]) || [],
    utilization:     (row.utilization as number) || 0,
    activeProjects:  0, // overwritten by getTeamMembers after project count
    hoursThisMonth:  (row.hours_this_month as number) || 0,
    status:          (row.status as TeamMember['status']) || 'active',
    joinedAt:        (row.created_at as string)?.split('T')[0] || new Date().toISOString().split('T')[0],
    avatar:          row.avatar_url as string | undefined,
  }
}

export async function getTeamMembers(): Promise<TeamMember[]> {
  const [{ data, error }, { data: projectData }] = await Promise.all([
    supabase.from('team_members').select('*').order('created_at'),
    supabase.from('projects').select('assignees, status'),
  ])
  if (error) throw error

  const activeProjs = (projectData ?? []).filter(p => p.status !== 'done')

  return (data ?? []).map(row => {
    const member = rowToMember(row)
    member.activeProjects = activeProjs.filter(
      p => (p.assignees as string[])?.includes(member.name)
    ).length
    return member
  })
}

export async function createTeamMember(m: Omit<TeamMember, 'id'>): Promise<TeamMember> {
  const { data, error } = await supabase
    .from('team_members')
    .insert({
      full_name:        m.name,
      email:            m.email || null,
      job_title:        m.role,
      skills:           m.skills,
      utilization:      m.utilization,
      hours_this_month: m.hoursThisMonth,
      status:           m.status,
    })
    .select()
    .single()
  if (error) throw error
  return rowToMember(data)
}

export async function updateTeamMember(id: string, changes: Partial<TeamMember>): Promise<void> {
  const updates: Record<string, unknown> = {}
  if (changes.name)           updates.full_name        = changes.name
  if (changes.role)           updates.job_title        = changes.role
  if (changes.email !== undefined) updates.email       = changes.email
  if (changes.skills)         updates.skills           = changes.skills
  if (changes.utilization !== undefined) updates.utilization = changes.utilization
  if (changes.hoursThisMonth !== undefined) updates.hours_this_month = changes.hoursThisMonth
  if (changes.status)         updates.status           = changes.status
  const { error } = await supabase.from('team_members').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteTeamMember(id: string): Promise<void> {
  const { error } = await supabase.from('team_members').delete().eq('id', id)
  if (error) throw error
}
